import { useState } from 'react';
import {
  Box, Button, Divider, Grid, IconButton, MenuItem, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Alert,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Add, Delete, Edit } from '@mui/icons-material';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import type { DeliveryIncentiveForm, DeliveryPartnerPricingForm, HubServiceOption, ServiceSchedulePricing, ZoneFormState } from '../zoneTypes';
import {
  DELIVERY_INCENTIVE_OPTIONS, activeServicePricing, allIncentiveTypesUsed, firstAvailableIncentiveType,
  dedupeIncentivesByType, formHasSelectedFoodGrocery, formHasSelectedPorter, incentiveTypeLabel,
  isFoodOrGroceryDelivery, isPorterService, newDeliveryIncentive, normalizeIncentiveType,
  validateIncentiveListUnique, validateIncentiveTypeUnique,
} from '../zoneTypes';
import { ZoneAmountNumberField, ZoneDistanceNumberField } from './ZonePricingNumberField';
import { chargeFieldLabel, formatMoney } from '../../../utils/currency';

interface Props {
  form: ZoneFormState;
  setForm: (f: ZoneFormState) => void;
  allServices: HubServiceOption[];
  unit: string;
  currency: string;
}

function serviceLabel(sp: ServiceSchedulePricing) {
  if (sp.slug === 'grocery-delivery') return 'Grocery Delivery';
  if (sp.slug === 'food-delivery') return 'Food Delivery';
  if (sp.slug === 'porter-service') return 'Porter';
  return sp.serviceName || sp.slug;
}

function ActiveStatusSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (active: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Switch
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        inputProps={{ 'aria-label': checked ? 'Active' : 'Inactive' }}
      />
      <Typography
        variant="body2"
        fontWeight={500}
        color={checked ? 'success.main' : 'text.secondary'}
      >
        {checked ? 'Active' : 'Inactive'}
      </Typography>
    </Stack>
  );
}

function PartnerPricingFields({
  pricing,
  onChange,
  unit,
  currency,
}: {
  pricing: DeliveryPartnerPricingForm;
  onChange: (patch: Partial<DeliveryPartnerPricingForm>) => void;
  unit: string;
  currency: string;
}) {
  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Status
        </Typography>
        <ActiveStatusSwitch
          checked={pricing.isActive}
          onChange={(isActive) => onChange({ isActive })}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Turn off to disable partner pricing for this service in the zone.
        </Typography>
      </Box>
      <Grid container spacing={1.5}>
        <Grid item xs={6} md={4}>
          <ZoneDistanceNumberField
            label={`Base KM (${unit})`}
            value={pricing.baseKm}
            onValueChange={(n) => onChange({ baseKm: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Base Amount', currency)}
            currency={currency}
            value={pricing.baseAmount}
            onValueChange={(n) => onChange({ baseAmount: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel(`Per-${unit} charge`, currency)}
            currency={currency}
            value={pricing.remainingKmRate ?? 0}
            onValueChange={(n) => onChange({ remainingKmRate: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Pickup Charge', currency)}
            currency={currency}
            value={pricing.pickupCharge}
            onValueChange={(n) => onChange({ pickupCharge: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Drop Charge', currency)}
            currency={currency}
            value={pricing.dropCharge}
            onValueChange={(n) => onChange({ dropCharge: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Waiting / Min', currency)}
            currency={currency}
            value={pricing.waitingChargePerMinute}
            onValueChange={(n) => onChange({ waitingChargePerMinute: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneAmountNumberField
            label={chargeFieldLabel('Minimum Guarantee', currency)}
            currency={currency}
            value={pricing.minimumGuarantee}
            onValueChange={(n) => onChange({ minimumGuarantee: n })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <ZoneDistanceNumberField
            label={`Maximum Delivery ${unit}`}
            value={pricing.maximumDeliveryKm}
            onValueChange={(n) => onChange({ maximumDeliveryKm: n })}
          />
        </Grid>
      </Grid>
    </>
  );
}

function IncentiveEditor({
  incentives,
  onChange,
  currency,
}: {
  incentives: DeliveryIncentiveForm[];
  onChange: (next: DeliveryIncentiveForm[]) => void;
  currency: string;
}) {
  const [incentiveDraft, setIncentiveDraft] = useState<DeliveryIncentiveForm | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeliveryIncentiveForm | null>(null);

  const typeError = incentiveDraft
    ? validateIncentiveTypeUnique(incentiveDraft.incentiveType, incentives, incentiveDraft.id)
    : undefined;
  const listError = validateIncentiveListUnique(incentives);
  const addDisabled = !!incentiveDraft || allIncentiveTypesUsed(incentives);

  const saveIncentive = () => {
    if (!incentiveDraft || typeError) return;
    const normalizedDraft = {
      ...incentiveDraft,
      incentiveType: normalizeIncentiveType(incentiveDraft.incentiveType),
    };
    const draftErr = validateIncentiveTypeUnique(
      normalizedDraft.incentiveType,
      incentives,
      normalizedDraft.id,
    );
    if (draftErr) return;

    const exists = incentives.some((i) => i.id === normalizedDraft.id);
    const next = exists
      ? incentives.map((i) => (i.id === normalizedDraft.id ? normalizedDraft : i))
      : [...incentives, normalizedDraft];
    const nextErr = validateIncentiveListUnique(next);
    if (nextErr) return;

    onChange(dedupeIncentivesByType(next));
    setIncentiveDraft(null);
    setEditingId(null);
  };

  const toggleIncentiveActive = (id: string, isActive: boolean) => {
    onChange(incentives.map((i) => (i.id === id ? { ...i, isActive } : i)));
    if (incentiveDraft?.id === id) {
      setIncentiveDraft({ ...incentiveDraft, isActive });
    }
  };

  const confirmDeleteIncentive = () => {
    if (!deleteTarget) return;
    onChange(incentives.filter((i) => i.id !== deleteTarget.id));
    if (incentiveDraft?.id === deleteTarget.id) {
      setIncentiveDraft(null);
      setEditingId(null);
    }
    if (editingId === deleteTarget.id) {
      setEditingId(null);
    }
    setDeleteTarget(null);
  };

  const deleteTargetLabel = deleteTarget
    ? incentiveTypeLabel(deleteTarget.incentiveType)
    : '';

  const duplicateIds = (() => {
    const counts = new Map<string, number>();
    incentives.forEach((i) => {
      const key = normalizeIncentiveType(i.incentiveType);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const dupKeys = new Set(
      [...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key),
    );
    return new Set(
      incentives
        .filter((i) => dupKeys.has(normalizeIncentiveType(i.incentiveType)))
        .map((i) => i.id),
    );
  })();

  return (
    <>
      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError} Remove or edit the duplicate row before continuing.
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>Incentive Options</Typography>
          <Typography variant="body2" color="text.secondary">
            Peak hour, rain, night, heavy order, multi order, festival bonuses.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            const draft = newDeliveryIncentive({
              incentiveType: firstAvailableIncentiveType(incentives),
            });
            setEditingId(draft.id);
            setIncentiveDraft(draft);
          }}
          disabled={addDisabled}
        >
          Add Incentive
        </Button>
      </Stack>

      {incentiveDraft && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Incentive Type"
                value={incentiveDraft.incentiveType}
                onChange={(e) => setIncentiveDraft({
                  ...incentiveDraft,
                  incentiveType: normalizeIncentiveType(e.target.value),
                })}
                error={Boolean(typeError)}
                helperText={typeError || 'Each type can only be added once'}
              >
                {DELIVERY_INCENTIVE_OPTIONS.map((o) => {
                  const used = incentives.some(
                    (i) => normalizeIncentiveType(i.incentiveType) === o.value && i.id !== incentiveDraft.id,
                  );
                  return (
                    <MenuItem key={o.value} value={o.value} disabled={used}>
                      {o.label}{used ? ' (already added)' : ''}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <ZoneAmountNumberField
                label={chargeFieldLabel('Amount', currency)}
                currency={currency}
                value={incentiveDraft.amount}
                onValueChange={(n) => setIncentiveDraft({ ...incentiveDraft, amount: n })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Status
              </Typography>
              <ActiveStatusSwitch
                checked={incentiveDraft.isActive}
                onChange={(isActive) => setIncentiveDraft({ ...incentiveDraft, isActive })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" onClick={saveIncentive} disabled={Boolean(typeError)}>Save</Button>
                <Button size="small" onClick={() => { setIncentiveDraft(null); setEditingId(null); }}>Cancel</Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {incentives.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No incentives yet. Click Add Incentive to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {incentives.map((inc, idx) => {
              const label = incentiveTypeLabel(inc.incentiveType);
              const isDuplicate = duplicateIds.has(inc.id);
              return (
                <TableRow key={inc.id} hover selected={editingId === inc.id}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    {label}
                    {isDuplicate && (
                      <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1 }}>
                        (duplicate)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatMoney(inc.amount, currency)}</TableCell>
                  <TableCell>
                    <ActiveStatusSwitch
                      checked={inc.isActive}
                      onChange={(isActive) => toggleIncentiveActive(inc.id, isActive)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => { setEditingId(inc.id); setIncentiveDraft({ ...inc }); }}
                      aria-label="Edit"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(inc);
                      }}
                      aria-label="Delete"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete incentive?"
        message={
          deleteTarget
            ? `Delete "${deleteTargetLabel}" incentive (${formatMoney(deleteTarget.amount, currency)})? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={confirmDeleteIncentive}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

function ExampleDetailLine({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        py: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary" sx={{ textAlign: 'right', fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

function EarningsExample({
  pricing,
  incentives,
  unit,
  currency,
}: {
  pricing: DeliveryPartnerPricingForm;
  incentives: DeliveryIncentiveForm[];
  unit: string;
  currency: string;
}) {
  const theme = useTheme();
  const exampleDistance = 7;
  const remainingKm = Math.max(0, exampleDistance - pricing.baseKm);
  const remainingCharge = remainingKm * pricing.remainingKmRate;
  const subtotal = pricing.baseAmount + pricing.pickupCharge + pricing.dropCharge + remainingCharge;
  const peakIncentive = incentives.find((i) => i.incentiveType === 'peak_hour' && i.isActive)?.amount ?? 0;
  const rainIncentive = incentives.find((i) => i.incentiveType === 'rain' && i.isActive)?.amount ?? 0;
  const withIncentives = subtotal + peakIncentive + rainIncentive;
  const finalEarnings = pricing.minimumGuarantee > 0 && withIncentives < pricing.minimumGuarantee
    ? pricing.minimumGuarantee
    : withIncentives;
  const guaranteeApplied = pricing.minimumGuarantee > 0 && withIncentives < pricing.minimumGuarantee;

  const distanceLine = exampleDistance <= pricing.baseKm
    ? `Within base ${pricing.baseKm} ${unit} → ${formatMoney(pricing.baseAmount, currency)}`
    : `Base ${pricing.baseKm} ${unit} = ${formatMoney(pricing.baseAmount, currency)} · `
      + `Remaining ${remainingKm} ${unit} × ${formatMoney(pricing.remainingKmRate, currency)} = ${formatMoney(remainingCharge, currency)}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 3,
        p: 2.5,
        bgcolor: theme.palette.mode === 'light'
          ? alpha(theme.palette.info.main, 0.1)
          : alpha(theme.palette.info.main, 0.2),
        borderColor: theme.palette.info.main,
        borderWidth: 1.5,
        borderStyle: 'solid',
        borderRadius: 2,
        boxShadow: theme.palette.mode === 'light'
          ? '0 2px 8px rgba(11,18,32,0.06)'
          : 'none',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} color="text.primary" gutterBottom>
        Example partner earnings — {exampleDistance} {unit} delivery
      </Typography>
      <Typography variant="body2" color="text.primary" sx={{ mb: 2, opacity: 0.85, lineHeight: 1.6 }}>
        Preview only. Calculated from the partner pricing and active incentives configured above.
      </Typography>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1.5, px: 2, py: 0.5, border: 1, borderColor: 'divider' }}>
        <ExampleDetailLine label="Distance charge" value={distanceLine} />
        <ExampleDetailLine
          label="Pickup + Drop"
          value={`${formatMoney(pricing.pickupCharge, currency)} + ${formatMoney(pricing.dropCharge, currency)} → Subtotal ${formatMoney(subtotal, currency)}`}
        />
        {(peakIncentive > 0 || rainIncentive > 0) && (
          <ExampleDetailLine
            label="Active incentives"
            value={[
              peakIncentive > 0 ? `Peak ${formatMoney(peakIncentive, currency)}` : '',
              rainIncentive > 0 ? `Rain ${formatMoney(rainIncentive, currency)}` : '',
            ].filter(Boolean).join(' · ')}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            pt: 1.25,
            pb: 0.75,
          }}
        >
          <Typography variant="body1" fontWeight={700} color="text.primary">
            Partner earnings
          </Typography>
          <Typography variant="body1" fontWeight={700} color="info.main">
            {formatMoney(finalEarnings, currency)}
          </Typography>
        </Box>
        {guaranteeApplied && (
          <Typography variant="body2" color="text.primary" sx={{ pb: 0.75, fontStyle: 'italic' }}>
            Minimum guarantee applied ({formatMoney(pricing.minimumGuarantee, currency)}).
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default function ZoneDeliveryPartnerStep({ form, setForm, allServices, unit, currency }: Props) {
  const hasFoodGrocery = formHasSelectedFoodGrocery(form, allServices);
  const hasPorter = formHasSelectedPorter(form, allServices);
  const foodGroceryServices = activeServicePricing(form, isFoodOrGroceryDelivery, allServices);
  const porterServices = activeServicePricing(form, isPorterService, allServices);
  const hasGrocery = foodGroceryServices.some((s) => s.slug === 'grocery-delivery');
  const hasFood = foodGroceryServices.some((s) => s.slug === 'food-delivery');

  const updateCustomerFeeForService = (serviceId: string, patch: Record<string, number>) => {
    if (!form.selectedServiceIds.includes(serviceId)) return;
    setForm({
      ...form,
      servicePricing: form.servicePricing.map((sp) => {
        if (sp.serviceId !== serviceId) return sp;
        return { ...sp, rideNow: { ...sp.rideNow, ...patch } };
      }),
    });
  };

  const foodTitle = hasGrocery && !hasFood
    ? 'Grocery Delivery — Zone Partner Pricing'
    : hasFood && !hasGrocery
      ? 'Food Delivery — Zone Partner Pricing'
      : 'Food & Grocery Delivery — Zone Partner Pricing';

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Zone Partner Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Customer fees and partner earnings are configured here for Food, Grocery, and Porter.
        Porter hub Pricing Rules are removed — use this step only.
      </Typography>

      {(foodGroceryServices.length > 0 || porterServices.length > 0) && (
        <>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Customer Fee</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Charged to the customer. Configure each selected delivery service separately.
          </Typography>
          {foodGroceryServices.map((sp) => (
            <Box key={sp.serviceId} sx={{ mb: 2.5 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>{serviceLabel(sp)}</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} md={4}>
                  <ZoneDistanceNumberField
                    label={`Base distance (${unit})`}
                    value={sp.rideNow?.baseDistanceKm ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { baseDistanceKm: n })}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <ZoneAmountNumberField
                    label={chargeFieldLabel('Delivery charge', currency)}
                    currency={currency}
                    value={sp.rideNow?.deliveryCharge ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { deliveryCharge: n })}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <ZoneAmountNumberField
                    label={chargeFieldLabel(`Per-${unit} charge after base`, currency)}
                    currency={currency}
                    value={sp.rideNow?.perKmRate ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { perKmRate: n })}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
          {porterServices.map((sp) => (
            <Box key={sp.serviceId} sx={{ mb: 2.5 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>{serviceLabel(sp)}</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}>
                  <ZoneAmountNumberField
                    label={chargeFieldLabel('Base fare', currency)}
                    currency={currency}
                    value={sp.rideNow?.baseFare ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { baseFare: n })}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <ZoneAmountNumberField
                    label={chargeFieldLabel(`Per-${unit} charge`, currency)}
                    currency={currency}
                    value={sp.rideNow?.perKmRate ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { perKmRate: n })}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <ZoneAmountNumberField
                    label={chargeFieldLabel('Per min', currency)}
                    currency={currency}
                    value={sp.rideNow?.perMinRate ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { perMinRate: n })}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <ZoneAmountNumberField
                    label={chargeFieldLabel('Per kg', currency)}
                    currency={currency}
                    value={sp.rideNow?.perKgRate ?? 0}
                    onValueChange={(n) => updateCustomerFeeForService(sp.serviceId, { perKgRate: n })}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {hasFoodGrocery && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>{foodTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Partner earnings for food &amp; grocery deliveries in this zone.
          </Typography>
          <PartnerPricingFields
            pricing={form.deliveryPartnerPricing}
            onChange={(patch) => {
              if (!formHasSelectedFoodGrocery(form, allServices)) return;
              setForm({
                ...form,
                deliveryPartnerPricing: { ...form.deliveryPartnerPricing, ...patch },
              });
            }}
            unit={unit}
            currency={currency}
          />
          <Divider sx={{ my: 3 }} />
          <IncentiveEditor
            incentives={form.deliveryIncentives}
            onChange={(deliveryIncentives) => {
              if (!formHasSelectedFoodGrocery(form, allServices)) return;
              setForm({ ...form, deliveryIncentives });
            }}
            currency={currency}
          />
          <EarningsExample
            pricing={form.deliveryPartnerPricing}
            incentives={form.deliveryIncentives}
            unit={unit}
            currency={currency}
          />
        </Box>
      )}

      {hasPorter && (
        <Box>
          {hasFoodGrocery && <Divider sx={{ my: 3 }} />}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Porter — Zone Partner Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Partner earnings for Porter parcel jobs in this zone (separate from food/grocery).
          </Typography>
          <PartnerPricingFields
            pricing={form.porterPartnerPricing}
            onChange={(patch) => {
              if (!formHasSelectedPorter(form, allServices)) return;
              setForm({
                ...form,
                porterPartnerPricing: { ...form.porterPartnerPricing, ...patch },
              });
            }}
            unit={unit}
            currency={currency}
          />
          <Divider sx={{ my: 3 }} />
          <IncentiveEditor
            incentives={form.porterIncentives}
            onChange={(porterIncentives) => {
              if (!formHasSelectedPorter(form, allServices)) return;
              setForm({ ...form, porterIncentives });
            }}
            currency={currency}
          />
          <EarningsExample
            pricing={form.porterPartnerPricing}
            incentives={form.porterIncentives}
            unit={unit}
            currency={currency}
          />
        </Box>
      )}
    </Box>
  );
}
